/** The TIMING half of an overlay — structurally satisfied by both
 *  `TextOverlaySpec` (lib/channels.ts) and `RenderTextOverlay`
 *  (remotion/types.ts). Declared locally on purpose: this module is imported
 *  by the Remotion composition, which the browser Studio also type-checks, so
 *  it must not reach channels.ts (node:fs → paths → storage). */
export interface OverlayTiming {
  /** Seconds into the CONTENT, or `{ fromEnd }` = seconds before it ends. */
  start: number | { fromEnd: number };
  /** Seconds on screen. */
  durationSec: number;
}

/** Frame window of a timed overlay inside the CONTENT (cover-hold excluded —
 *  the composition mounts overlays inside the same content tree as captions,
 *  so a cover-hold prefix shifts them automatically). */
export function overlayWindow(
  spec: OverlayTiming,
  durationSec: number,
  fps: number,
): { from: number; durationInFrames: number } {
  const total = Math.round(durationSec * fps);
  // A `fromEnd` LONGER than the clip is the common case for a short clip and
  // the default 9 s CTA: `durationSec - fromEnd` goes negative, and clamping
  // that to 0 put the "call to action" at the START of the video (QA D-9).
  // An end-anchored overlay stays end-anchored: it starts as late as it can
  // while still playing in full.
  const startSec = ((): number => {
    if (typeof spec.start === "number") return spec.start;
    const anchored = durationSec - spec.start.fromEnd;
    return anchored >= 0 ? anchored : Math.max(0, durationSec - spec.durationSec);
  })();
  const from = Math.min(total, Math.max(0, Math.round(startSec * fps)));
  const durationInFrames = Math.max(1, Math.min(total - from, Math.round(spec.durationSec * fps)));
  return { from, durationInFrames };
}
